import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  createGugikLayers,
  GugikLayerPanel,
  useGugikLoading,
  useGugikState,
  useGugikToggle,
} from "@/components/map/gugik";
import { escapeHtml } from "@/features/contractor_map/components/mapStyle";
import {
  fallbackLineStyle,
  fallbackLineSwatch,
  objectIcon,
  objectSymbolSvg,
} from "@/features/power_projects/components/MapTab";
import type { LedParcel, LedRouteLayers, TitleSummary } from "../types";

// Kolory stanów tytułu prawnego — słownik DLC 01 (pozyskany / w toku /
// problem-odmowa / nie rozpoczęto); poza trasą przerywane. Działka publiczna
// (instytucja) jest KRESKOWANA, a nie oznaczona kolorem — kolor zostaje na stan tytułu,
// więc na wydruku i przy daltonizmie nadal widać, która działka jest urzędowa.
const TITLE_STYLE: Record<TitleSummary, L.PathOptions> = {
  secured: { color: "#1e6b3a", weight: 1.4, fillColor: "#4caf7d", fillOpacity: 0.35 },
  in_progress: { color: "#c99b2c", weight: 1.4, fillColor: "#f2c14e", fillOpacity: 0.35 },
  problem: { color: "#b8101f", weight: 1.6, fillColor: "#e8707c", fillOpacity: 0.35 },
  not_started: { color: "#6b7280", weight: 1.2, fillColor: "#9ca3af", fillOpacity: 0.18 },
};
const PUBLIC_HATCH = {
  id: "led-public-parcel-hatch",
  rotate: 45,
  spacing: 7,
  stroke: "#1f2937",
  width: 1.4,
  opacity: 0.8,
};

/** Leaflet nie ma API wzorów wypełnienia — wzór wstrzykujemy raz do <svg> warstwy
 *  overlayPane i kierujemy na niego `fill` ścieżki (ten sam sposób co tablica DLC 01). */
function ensureHatchPattern(map: L.Map): void {
  const svg = map.getPane("overlayPane")?.querySelector("svg");
  if (!svg || svg.querySelector(`#${PUBLIC_HATCH.id}`)) return;
  const NS = "http://www.w3.org/2000/svg";
  let defs = svg.querySelector("defs");
  if (!defs) {
    defs = document.createElementNS(NS, "defs");
    svg.insertBefore(defs, svg.firstChild);
  }
  const pattern = document.createElementNS(NS, "pattern");
  pattern.setAttribute("id", PUBLIC_HATCH.id);
  pattern.setAttribute("patternUnits", "userSpaceOnUse");
  pattern.setAttribute("width", String(PUBLIC_HATCH.spacing));
  pattern.setAttribute("height", String(PUBLIC_HATCH.spacing));
  pattern.setAttribute("patternTransform", `rotate(${PUBLIC_HATCH.rotate})`);
  const line = document.createElementNS(NS, "line");
  line.setAttribute("x1", "0");
  line.setAttribute("y1", "0");
  line.setAttribute("x2", "0");
  line.setAttribute("y2", String(PUBLIC_HATCH.spacing));
  line.setAttribute("stroke", PUBLIC_HATCH.stroke);
  line.setAttribute("stroke-width", String(PUBLIC_HATCH.width));
  line.setAttribute("stroke-opacity", String(PUBLIC_HATCH.opacity));
  pattern.appendChild(line);
  defs.appendChild(pattern);
}
const OUT_OF_ROUTE_STYLE: L.PathOptions = {
  color: "#6b7280", weight: 1, dashArray: "5 4", fillColor: "#ffffff", fillOpacity: 0.05,
};
const ROUTE_STYLE: L.PathOptions = { color: "#c8102e", weight: 3.5 };

/** Dymek Leafletu domyślnie wygląda jak z 2011 roku — zero zaokrągleń, ramka i typografia
 *  jak w reszcie aplikacji. Wstrzykujemy raz, bo to style globalne wtyczki. */
const POPUP_CSS_ID = "led-map-popup-style";
function ensurePopupStyle(): void {
  if (document.getElementById(POPUP_CSS_ID)) return;
  const el = document.createElement("style");
  el.id = POPUP_CSS_ID;
  el.textContent = `
    .led-popup .leaflet-popup-content-wrapper {
      border-radius: 0;
      border: 1px solid var(--color-border-strong, #8a8a84);
      box-shadow: 0 10px 24px -12px rgba(0,0,0,.45);
      background: var(--color-bg-surface, #fff);
      color: var(--color-text-primary, #141413);
      padding: 0;
    }
    .led-popup .leaflet-popup-content { margin: 0; width: 15rem !important; }
    .led-popup .leaflet-popup-tip { background: var(--color-bg-surface, #fff); border: 1px solid var(--color-border-strong, #8a8a84); }
    .led-popup a.leaflet-popup-close-button { top: 4px; right: 4px; color: var(--color-text-muted, #6b6b66); }
    .led-popup .lp-head { border-bottom: 1px solid var(--color-border, #d9d8d0); padding: .5rem .7rem .45rem; }
    .led-popup .lp-teryt { font: 700 12px/1.2 ui-monospace, monospace; letter-spacing: .02em; }
    .led-popup .lp-sub { font-size: 11px; color: var(--color-text-muted, #6b6b66); margin-top: 2px; }
    .led-popup .lp-body { padding: .45rem .7rem .55rem; display: grid; gap: 3px; }
    .led-popup .lp-owner { display: flex; align-items: baseline; gap: .4rem; font-size: 12px; }
    .led-popup .lp-owner i { width: 6px; height: 6px; flex: none; display: inline-block; }
    .led-popup .lp-status { margin-left: auto; font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: var(--color-text-muted, #6b6b66); white-space: nowrap; }
    .led-popup .lp-foot { padding: .35rem .7rem .55rem; font-size: 11px; color: var(--color-text-muted, #6b6b66); }
  `;
  document.head.appendChild(el);
}

const OWNER_INK: Record<string, string> = {
  secured: "#1e6b3a",
  not_required: "#1e6b3a",
  in_progress: "#c99b2c",
  problem: "#b8101f",
  refused: "#b8101f",
  not_started: "#9ca3af",
};

function styleFor(parcel: LedParcel): L.PathOptions {
  if (!parcel.in_current_route) return OUT_OF_ROUTE_STYLE;
  return TITLE_STYLE[parcel.title_summary] ?? TITLE_STYLE.not_started;
}

const LEGEND: { swatch: string; label: string }[] = [
  { swatch: "border-[#1e6b3a] bg-[#bfe3cb]", label: "tytuł pozyskany" },
  { swatch: "border-[#c99b2c] bg-[#ffe6b3]", label: "w trakcie" },
  { swatch: "border-[#b8101f] bg-[#f5c2c7]", label: "problem / odmowa" },
  { swatch: "border-[#8a8a86] bg-[#e6e6df]", label: "nie rozpoczęto" },
  { swatch: "border-[#6b6b70] border-dashed bg-white", label: "działka poza trasą" },
];

/** Kreskowanie w legendzie rysujemy tym samym kątem co na mapie. */
const HATCH_SWATCH =
  "repeating-linear-gradient(45deg, #1f2937 0 1.4px, transparent 1.4px 7px)";

interface Props {
  route: GeoJSON.Geometry | null;
  layers?: LedRouteLayers | null;
  parcels: LedParcel[];
  onSelect?: (parcel: LedParcel) => void;
  className?: string;
}


/** Mapa projektu w widoku nadzorczym: trasa + wszystkie działki (w trasie i poza nią)
 *  kolorowane stanem tytułu prawnego. OSM, bez warstw WMS — jak „Mapa zamówień". */
export function LedMap({ route, layers: routeLayers, parcels, onSelect, className }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.LayerGroup | null>(null);
  const [zoom, setZoom] = useState(6);
  // Te same podkłady, co w warsztacie projektu: ortofoto, działki i uzbrojenie z GUGiK.
  const [gugik, setGugik] = useGugikState("contractorLed.map", {
    parcels: false,
    infra: false,
    ortho: false,
    mpzp: false,
    gdos: false,
  });
  const gugikRefs = {
    parcels: useRef<L.TileLayer.WMS | null>(null),
    infra: useRef<L.TileLayer.WMS | null>(null),
    ortho: useRef<L.TileLayer.WMS | null>(null),
    mpzp: useRef<L.TileLayer.WMS | null>(null),
    gdos: useRef<L.TileLayer.WMS | null>(null),
  };

  /** Legenda tego, co NAPRAWDĘ jest na tej trasie — typy linii i urządzeń z DXF-a. */
  const routeLegend = useMemo(() => {
    const counts = new Map<string, { kind: "line" | "object"; n: number }>();
    const bump = (code: string, kind: "line" | "object") => {
      const cur = counts.get(code);
      if (cur) cur.n += 1;
      else counts.set(code, { kind, n: 1 });
    };
    for (const f of routeLayers?.typed_lines?.features ?? []) bump(f.properties.semantic_type, "line");
    for (const o of routeLayers?.objects ?? []) bump(o.semantic_type, "object");
    const rows = [...counts.entries()].map(([code, { kind, n }]) => ({
      key: code,
      swatch: kind === "line" ? fallbackLineSwatch(code) : objectSymbolSvg(code),
      label: code.replace(/_/g, " ") || "obiekt",
      n,
    }));
    if (!routeLayers?.typed_lines?.features?.length && route) {
      rows.unshift({ key: "__route", swatch: fallbackLineSwatch(null), label: "trasa", n: 0 });
    }
    if (routeLayers?.demolition) {
      rows.push({ key: "__demolition", swatch: fallbackLineSwatch("likwidacja"), label: "demontaż / likwidacja", n: 0 });
    }
    return rows.sort((a, b) => a.label.localeCompare(b.label, "pl"));
  }, [routeLayers, route]);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    ensurePopupStyle();
    const map = L.map(containerRef.current, { center: [52, 19], zoom: 6, attributionControl: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    layersRef.current = L.layerGroup().addTo(map);
    const g = createGugikLayers(map);
    gugikRefs.parcels.current = g.parcels;
    gugikRefs.infra.current = g.infra;
    gugikRefs.ortho.current = g.ortho;
    gugikRefs.mpzp.current = g.mpzp;
    gugikRefs.gdos.current = g.gdos;
    map.on("zoomend", () => setZoom(map.getZoom()));
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layersRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layerGroup = layersRef.current;
    if (!map || !layerGroup) return;
    layerGroup.clearLayers();
    const bounds = L.latLngBounds([]);
    for (const parcel of parcels) {
      if (!parcel.geometry) continue;
      const layer = L.geoJSON(parcel.geometry as never, { style: styleFor(parcel) });
      // Działka urzędowa: kreskowana nakładka NAD kolorem stanu; nieinteraktywna, żeby
      // klik i popup dalej trafiały w działkę pod spodem.
      if (parcel.is_public && parcel.in_current_route) {
        const hatch = L.geoJSON(parcel.geometry as never, {
          style: { stroke: false, fillOpacity: 1 },
          interactive: false,
        });
        hatch.addTo(layerGroup);
        ensureHatchPattern(map);
        hatch.eachLayer((l) => {
          const path = (l as unknown as { _path?: SVGPathElement })._path;
          path?.setAttribute("fill", `url(#${PUBLIC_HATCH.id})`);
        });
      }
      const owners = parcel.owners
        .map(
          (o) =>
            `<span class="lp-owner"><i style="background:${OWNER_INK[o.title_status] ?? OWNER_INK.not_started}"></i>` +
            `<span>${escapeHtml(o.full_name)}</span>` +
            `<span class="lp-status">${escapeHtml(o.title_status_display)}</span></span>`,
        )
        .join("");
      const meta = [
        parcel.obreb,
        parcel.is_public ? "działka publiczna" : null,
        parcel.in_current_route
          ? parcel.intersection_length_m != null
            ? `${Math.round(parcel.intersection_length_m)} m w trasie`
            : null
          : "poza trasą",
      ]
        .filter(Boolean)
        .join(" · ");
      layer.bindPopup(
        `<div class="lp-head"><div class="lp-teryt">${escapeHtml(parcel.teryt)}</div>` +
          `<div class="lp-sub">${escapeHtml(meta)}</div></div>` +
          (owners
            ? `<div class="lp-body">${owners}</div>`
            : `<div class="lp-foot">brak stron w kartotece</div>`) +
          (onSelect ? `<div class="lp-foot">kliknij działkę, żeby otworzyć jej kartę</div>` : ""),
        { className: "led-popup", closeButton: true, autoPanPadding: [24, 24] },
      );
      if (onSelect) layer.on("click", () => onSelect(parcel));
      layer.addTo(layerGroup);
      if (parcel.in_current_route) bounds.extend(layer.getBounds());
    }
    const typed = routeLayers?.typed_lines?.features ?? [];
    if (typed.length > 0) {
      // Linie z typem rysujemy tak samo jak warsztat projektu: kolor i kreska mówią,
      // czy to kabel, czy napowietrzna, SN czy nN, budowa czy demontaż.
      const typedLayer = L.geoJSON(routeLayers!.typed_lines as never, {
        style: (f) => fallbackLineStyle((f?.properties as { semantic_type?: string })?.semantic_type),
      });
      typedLayer.addTo(layerGroup);
      bounds.extend(typedLayer.getBounds());
    } else if (route) {
      const routeLayer = L.geoJSON(route as never, { style: ROUTE_STYLE });
      routeLayer.addTo(layerGroup);
      bounds.extend(routeLayer.getBounds());
    }
    if (routeLayers?.demolition) {
      const dem = L.geoJSON(routeLayers.demolition as never, {
        style: { color: "#2563eb", weight: 3, dashArray: "6 4" },
      });
      dem.addTo(layerGroup);
    }
    // Urządzenia z DXF-a (słupy, stacje, złącza) — te same symbole co na tablicy projektu.
    for (const obj of routeLayers?.objects ?? []) {
      const coords = (obj.geometry as { coordinates?: [number, number] }).coordinates;
      if (!coords) continue;
      const marker = L.marker([coords[1], coords[0]], {
        icon: objectIcon(obj.semantic_type, 16),
        title: obj.label || obj.semantic_type,
      });
      marker.bindPopup(
        `<div class="lp-head"><div class="lp-teryt">${escapeHtml(obj.label || obj.semantic_type.replace(/_/g, " "))}</div>` +
          `<div class="lp-sub">${escapeHtml(obj.semantic_type.replace(/_/g, " "))}</div></div>`,
        { className: "led-popup", closeButton: true },
      );
      marker.addTo(layerGroup);
      bounds.extend(marker.getLatLng());
    }
    // Dopasowanie PO layoucie — fitBounds w ticku montażu liczy na złych wymiarach.
    const timer = window.setTimeout(() => {
      map.invalidateSize();
      if (bounds.isValid()) map.fitBounds(bounds.pad(0.15));
    }, 80);
    return () => window.clearTimeout(timer);
  }, [parcels, route, routeLayers, onSelect]);

  useGugikToggle(mapRef, gugikRefs.parcels, gugik.parcels);
  useGugikToggle(mapRef, gugikRefs.infra, gugik.infra);
  useGugikToggle(mapRef, gugikRefs.ortho, gugik.ortho);
  useGugikToggle(mapRef, gugikRefs.mpzp, gugik.mpzp);
  useGugikToggle(mapRef, gugikRefs.gdos, gugik.gdos);
  const gugikLoading = useGugikLoading(
    gugikRefs.parcels,
    gugikRefs.infra,
    gugikRefs.ortho,
    gugik,
    gugikRefs.mpzp,
    gugikRefs.gdos,
  );

  return (
    <div className={className}>
      {/* Panel warstw siedzi W ROGU mapy — dokładnie tam, gdzie w warsztacie projektu,
          pod kontrolką zoomu; nad mapą zabierałby pół ekranu.
          `isolate` zamyka z-indeksy Leafletu/GUGiK (panele do 1000) w ramce mapy, żeby
          panel powiadomień i modale (z-50) malowały się nad mapą — jak w MapTab. */}
      <div className="relative isolate z-0 overflow-hidden border border-[var(--color-border)]">
        <div ref={containerRef} className="h-[32rem] w-full" />
        <GugikLayerPanel
          gugik={gugik}
          onChange={setGugik}
          zoom={zoom}
          loading={gugikLoading}
          className="absolute left-2 top-[78px] z-[500]"
        />
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[var(--color-text-secondary)]">
        {LEGEND.map((l) => (
          <span key={l.label} className="inline-flex items-center gap-1.5">
            <i className={`inline-block h-3 w-3 border ${l.swatch}`} />
            {l.label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <i
            className="inline-block h-3 w-3 border border-[#1f2937]"
            style={{ backgroundImage: HATCH_SWATCH }}
          />
          działka publiczna (instytucja) — kreskowana
        </span>
      </div>
      {routeLegend.length > 0 && (
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--color-border-muted)] pt-1 text-[11px] text-[var(--color-text-secondary)]">
          <span className="section-label">trasa i urządzenia</span>
          {routeLegend.map((r) => (
            <span key={r.key} className="inline-flex items-center gap-1.5">
              <span className="inline-flex h-3 w-6 items-center justify-center" dangerouslySetInnerHTML={{ __html: r.swatch }} />
              {r.label}
              {r.n > 0 && <span className="font-mono text-[10px] text-[var(--color-text-muted)]">×{r.n}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
